import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LogOut,
  RefreshCw,
  Search,
  MessageCircle,
  Mail,
  Phone,
  MapPin,
  Building2,
  Users,
  TrendingUp,
  CalendarDays,
  CheckCircle2,
  XCircle,
  Inbox,
} from "lucide-react";
import Logo, { LogoFilterDefs } from "../components/landing/Logo";
import { useAuth } from "../lib/AuthContext";
import { auth, formatApiError } from "../lib/api";
import UsersPanel from "./admin/UsersPanel";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, today: 0, last_7_days: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setError("");
    setRefreshing(true);
    try {
      const [l, s] = await Promise.all([auth.listLeads(200), auth.stats()]);
      setLeads(l || []);
      setStats(s || { total: 0, today: 0, last_7_days: 0 });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter((l) =>
      [l.name, l.business, l.phone, l.city, l.email, l.source]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [leads, query]);

  const onLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  if (user === null) {
    return <div className="min-h-screen bg-[#0A0B0E] flex items-center justify-center text-white/40">...</div>;
  }
  if (user === false) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#0A0B0E] text-white">
      <LogoFilterDefs />

      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#0A0B0E]/85 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo size={32} />
            <span className="hidden sm:inline-block text-[11px] uppercase tracking-[0.2em] text-white/40 border-l border-white/10 pl-4">
              Panel Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline text-sm text-white/55" data-testid="admin-user-email">
              {user.email}
            </span>
            <button
              onClick={load}
              disabled={refreshing}
              data-testid="admin-refresh"
              className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-white/70 hover:text-white transition-colors disabled:opacity-50"
              title="Refrescar"
            >
              <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} />
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors px-3 py-1.5 rounded-full hover:bg-white/5"
            >
              <LogOut size={14} />
              Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-8 md:py-12">
        {/* Tabs (only if super_admin) */}
        {user.role === "super_admin" && (
          <div className="mb-7 flex items-center gap-2" data-testid="admin-tabs">
            <button
              onClick={() => setTab("leads")}
              data-testid="admin-tab-leads"
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                tab === "leads"
                  ? "bg-brand-orange text-white shadow-[0_0_18px_rgba(249,115,22,0.4)]"
                  : "bg-white/5 text-white/70 hover:text-white border border-white/10"
              }`}
            >
              Leads
            </button>
            <button
              onClick={() => setTab("users")}
              data-testid="admin-tab-users"
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                tab === "users"
                  ? "bg-brand-orange text-white shadow-[0_0_18px_rgba(249,115,22,0.4)]"
                  : "bg-white/5 text-white/70 hover:text-white border border-white/10"
              }`}
            >
              Usuarios
            </button>
          </div>
        )}

        {tab === "users" && user.role === "super_admin" ? (
          <UsersPanel currentUser={user} />
        ) : (
        <>
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-8"
        >
          <span className="text-[11px] uppercase tracking-[0.22em] text-brand-orange/90 font-medium">
            Dashboard
          </span>
          <h1 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tighter text-white">
            Leads recibidos
          </h1>
          <p className="mt-1.5 text-sm md:text-base text-white/55">
            Lista de prospectos capturados desde la landing.
          </p>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <StatCard
            icon={Users}
            label="Total"
            value={stats.total}
            color="text-brand-orange"
            bg="bg-brand-orange/10"
            testid="stat-total"
          />
          <StatCard
            icon={CalendarDays}
            label="Hoy"
            value={stats.today}
            color="text-emerald-400"
            bg="bg-emerald-500/10"
            testid="stat-today"
          />
          <StatCard
            icon={TrendingUp}
            label="Últimos 7 días"
            value={stats.last_7_days}
            color="text-blue-400"
            bg="bg-blue-500/10"
            testid="stat-week"
          />
        </div>

        {/* Search */}
        <div className="relative mb-5">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/35" />
          <input
            type="search"
            placeholder="Buscar por nombre, negocio, ciudad, teléfono..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="admin-search"
            className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/[0.04] border border-white/10 focus:border-brand-orange/60 text-white text-[15px] placeholder:text-white/30 outline-none transition-colors"
          />
        </div>

        {error && (
          <div className="mb-5 rounded-xl bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="rounded-2xl bg-surface/60 border border-white/[0.07] overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-white/40 text-sm">Cargando...</div>
          ) : filtered.length === 0 ? (
            <EmptyState hasQuery={!!query} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="leads-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-white/40 border-b border-white/5">
                    <Th>Nombre</Th>
                    <Th>Negocio</Th>
                    <Th>Contacto</Th>
                    <Th>Ciudad</Th>
                    <Th>Estado</Th>
                    <Th>Fecha</Th>
                    <Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((lead, i) => (
                    <LeadRow key={lead.id} lead={lead} onSelect={setSelected} testid={`lead-row-${i}`} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="mt-4 text-xs text-white/35">
          Mostrando {filtered.length} de {leads.length} leads
        </p>
        </>
        )}
      </main>

      {selected && (
        <LeadDrawer lead={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, bg, testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="rounded-2xl bg-surface/60 border border-white/[0.07] p-5 flex items-center gap-4"
      data-testid={testid}
    >
      <div className={`w-12 h-12 rounded-xl ${bg} flex items-center justify-center`}>
        <Icon className={color} size={22} strokeWidth={1.75} />
      </div>
      <div>
        <p className="text-xs uppercase tracking-wider text-white/45">{label}</p>
        <p className="mt-0.5 text-2xl font-semibold text-white tracking-tight">{value}</p>
      </div>
    </motion.div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 font-medium">{children}</th>;
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3.5 align-middle ${className}`}>{children}</td>
);

function LeadRow({ lead, onSelect, testid }) {
  const phoneDigits = (lead.phone || "").replace(/\D/g, "");
  const date = new Date(lead.created_at);
  const isToday = new Date().toDateString() === date.toDateString();
  return (
    <tr
      onClick={() => onSelect(lead)}
      className="border-b border-white/[0.05] last:border-0 hover:bg-white/[0.025] transition-colors cursor-pointer"
      data-testid={testid}
    >
      <Td><span className="font-medium text-white">{lead.name}</span></Td>
      <Td><span className="text-white/80">{lead.business}</span></Td>
      <Td>
        <div className="flex flex-col gap-0.5">
          <span className="text-white/80">{lead.phone}</span>
          {lead.email && <span className="text-white/45 text-xs">{lead.email}</span>}
        </div>
      </Td>
      <Td><span className="text-white/70">{lead.city}</span></Td>
      <Td>
        <div className="flex flex-col gap-1">
          <Badge ok={lead.email_status === "sent"} label="Notif" />
          {lead.autoreply_status !== "skipped" && (
            <Badge ok={lead.autoreply_status === "sent"} label="Auto" />
          )}
        </div>
      </Td>
      <Td className="text-white/55 text-xs">
        {isToday ? "Hoy " : date.toLocaleDateString() + " "}
        {date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
      </Td>
      <Td>
        <a
          href={`https://wa.me/${phoneDigits}`}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#25D366]/20 text-[#25D366] hover:bg-[#25D366]/30 transition-colors"
          title="Responder por WhatsApp"
        >
          <MessageCircle size={14} />
        </a>
      </Td>
    </tr>
  );
}

function Badge({ ok, label }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium tracking-wide uppercase ${
        ok
          ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
          : "bg-red-500/15 text-red-300 border border-red-500/30"
      }`}
    >
      {ok ? <CheckCircle2 size={9} /> : <XCircle size={9} />}
      {label}
    </span>
  );
}

function EmptyState({ hasQuery }) {
  return (
    <div className="p-14 text-center">
      <div className="w-14 h-14 rounded-full bg-white/5 border border-white/10 mx-auto flex items-center justify-center mb-4">
        <Inbox size={22} className="text-white/40" />
      </div>
      <p className="text-white/60 font-medium">
        {hasQuery ? "No hay resultados para tu búsqueda" : "Aún no hay leads"}
      </p>
      <p className="mt-1 text-sm text-white/40">
        {hasQuery ? "Prueba con otro término" : "Cuando alguien llene el formulario, aparecerá aquí."}
      </p>
    </div>
  );
}

function LeadDrawer({ lead, onClose }) {
  const phoneDigits = (lead.phone || "").replace(/\D/g, "");
  const date = new Date(lead.created_at);
  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose} data-testid="lead-drawer">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <motion.aside
        initial={{ x: 480 }}
        animate={{ x: 0 }}
        exit={{ x: 480 }}
        transition={{ type: "tween", duration: 0.3 }}
        className="relative w-full max-w-md bg-[#0E1015] border-l border-white/10 h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-5 border-b border-white/5">
          <div className="flex items-start justify-between">
            <div>
              <span className="text-[11px] uppercase tracking-wider text-brand-orange/90 font-medium">Lead</span>
              <h2 className="mt-1 text-2xl font-semibold text-white">{lead.name}</h2>
              <p className="text-sm text-white/55 mt-0.5">{lead.business}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white/50 hover:text-white transition-colors text-xl leading-none p-1"
              data-testid="lead-drawer-close"
              aria-label="cerrar"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <Row icon={Phone} label="Teléfono / WhatsApp" value={lead.phone} />
          {lead.email && <Row icon={Mail} label="Email" value={lead.email} mono />}
          <Row icon={MapPin} label="Ciudad" value={lead.city} />
          <Row icon={Building2} label="Idioma" value={lead.lang?.toUpperCase()} />
          <Row label="Fuente" value={lead.source} />
          <Row label="Fecha" value={date.toLocaleString()} />
          <Row label="ID" value={lead.id} mono small />

          <div className="pt-4 flex flex-col sm:flex-row gap-2.5">
            <a
              href={`https://wa.me/${phoneDigits}?text=Hola%20${encodeURIComponent(lead.name)}%2C%20gracias%20por%20tu%20inter%C3%A9s%20en%20VexlyPOS.`}
              target="_blank"
              rel="noreferrer"
              data-testid="drawer-whatsapp"
              className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5b] text-white rounded-full px-5 py-2.5 text-sm font-medium transition-colors"
            >
              <MessageCircle size={15} />
              Responder por WhatsApp
            </a>
            {lead.email && (
              <a
                href={`mailto:${lead.email}`}
                className="inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-full px-5 py-2.5 text-sm font-medium transition-colors"
              >
                <Mail size={15} />
                Enviar email
              </a>
            )}
          </div>

          <div className="pt-4 mt-2 border-t border-white/5 flex flex-col gap-2 text-xs text-white/50">
            <div className="flex items-center gap-2">
              <span className="w-24 text-white/40">Notificación:</span>
              <Badge ok={lead.email_status === "sent"} label={lead.email_status} />
            </div>
            <div className="flex items-center gap-2">
              <span className="w-24 text-white/40">Auto-respuesta:</span>
              <Badge ok={lead.autoreply_status === "sent"} label={lead.autoreply_status} />
            </div>
          </div>
        </div>
      </motion.aside>
    </div>
  );
}

function Row({ icon: Icon, label, value, mono, small }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center flex-shrink-0">
        {Icon ? <Icon size={14} className="text-white/60" /> : <span className="w-1.5 h-1.5 rounded-full bg-brand-orange/70" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-wider text-white/40">{label}</p>
        <p
          className={`mt-0.5 text-white break-words ${mono ? "font-mono" : ""} ${small ? "text-xs text-white/60" : "text-[15px]"}`}
        >
          {value}
        </p>
      </div>
    </div>
  );
}
